/**
 * RNCM Productivity Constants and Helper Functions
 */

export const API_URL = process.env.REACT_APP_BACKEND_URL;

// Futuristic vibrant color palette
export const COLORS = ['#00D4FF', '#FF00D4', '#00FF94', '#FFD400', '#9D00FF', '#FF4D00'];
export const TEAMS = ['Blue', 'Yellow', 'Green'];
export const DISCIPLINES = ['RNCM', 'LPN', 'Wound RN', 'PRN'];

// Futuristic team color mapping with gradients
export const getTeamColor = (team) => {
  const colors = {
    'Blue': { 
      bg: 'bg-gradient-to-r from-cyan-400 to-blue-500', 
      text: 'text-white', 
      border: 'border-cyan-400',
      glow: 'shadow-cyan-400/50',
      hex: '#00D4FF'
    },
    'Yellow': { 
      bg: 'bg-gradient-to-r from-yellow-300 to-amber-500', 
      text: 'text-black', 
      border: 'border-yellow-400',
      glow: 'shadow-yellow-400/50',
      hex: '#FFD400'
    },
    'Green': { 
      bg: 'bg-gradient-to-r from-emerald-400 to-green-500', 
      text: 'text-white', 
      border: 'border-emerald-400',
      glow: 'shadow-emerald-400/50',
      hex: '#00FF94'
    },
  };
  return colors[team] || { bg: 'bg-gradient-to-r from-gray-400 to-gray-600', text: 'text-white', border: 'border-gray-400', glow: 'shadow-gray-400/50', hex: '#888888' };
};

// Futuristic gradient presets for cards and sections
export const FUTURISTIC_GRADIENTS = {
  primary: 'from-cyan-500 via-blue-600 to-violet-600',
  secondary: 'from-fuchsia-500 via-purple-600 to-blue-600',
  success: 'from-emerald-400 via-green-500 to-teal-500',
  warning: 'from-amber-400 via-orange-500 to-rose-500',
  info: 'from-sky-400 via-cyan-500 to-blue-500',
  dark: 'from-slate-800 via-slate-900 to-black',
  neon: 'from-pink-500 via-purple-500 to-cyan-500'
};

/**
 * Get the next Medicare week (Sunday-Saturday) after a given date
 * Medicare weeks run Sunday-Saturday
 */
export const getNextMedicareWeek = (fromDate = new Date()) => {
  const date = new Date(fromDate);
  
  // Find next Sunday (start of Medicare week)
  const dayOfWeek = date.getDay(); // 0 = Sunday
  let daysUntilNextSunday = (7 - dayOfWeek) % 7;
  
  // If today is Sunday, go to NEXT Sunday (next week)
  if (daysUntilNextSunday === 0) {
    daysUntilNextSunday = 7;
  }
  
  const nextSunday = new Date(date);
  nextSunday.setDate(date.getDate() + daysUntilNextSunday);
  
  // Saturday is 6 days after Sunday
  const nextSaturday = new Date(nextSunday);
  nextSaturday.setDate(nextSunday.getDate() + 6);
  
  return {
    start: nextSunday.toISOString().split('T')[0],
    end: nextSaturday.toISOString().split('T')[0]
  };
};

/**
 * Get the Medicare week following a source period
 * Takes the source period end date and calculates the next week
 */
export const getNextMedicareWeekFromPeriod = (sourcePeriodStart) => {
  if (!sourcePeriodStart) {
    return getNextMedicareWeek();
  }
  
  // Parse the source period and get the Sunday of that week
  const sourceDate = new Date(sourcePeriodStart + 'T00:00:00');
  
  // Move to the next week's Sunday
  const nextSunday = new Date(sourceDate);
  nextSunday.setDate(sourceDate.getDate() + 7);
  
  // Ensure it lands on Sunday
  const dayOffset = nextSunday.getDay();
  if (dayOffset !== 0) {
    nextSunday.setDate(nextSunday.getDate() - dayOffset);
  }
  
  const nextSaturday = new Date(nextSunday);
  nextSaturday.setDate(nextSunday.getDate() + 6);
  
  return {
    start: nextSunday.toISOString().split('T')[0],
    end: nextSaturday.toISOString().split('T')[0]
  };
};

/**
 * Format a date range as a Medicare week display string
 */
export const formatMedicareWeek = (start, end) => {
  if (!start || !end) return '';
  const startDate = new Date(start + 'T00:00:00');
  const endDate = new Date(end + 'T00:00:00');
  const options = { month: 'short', day: 'numeric' };
  return `${startDate.toLocaleDateString('en-US', options)} - ${endDate.toLocaleDateString('en-US', options)}`;
};

/**
 * Get the current Medicare week (containing today)
 */
export const getCurrentMedicareWeek = () => {
  const today = new Date();
  const dayOfWeek = today.getDay(); // 0 = Sunday
  
  // Find the Sunday of this week
  const sunday = new Date(today);
  sunday.setDate(today.getDate() - dayOfWeek);
  
  // Saturday is 6 days after Sunday
  const saturday = new Date(sunday);
  saturday.setDate(sunday.getDate() + 6);
  
  return {
    start: sunday.toISOString().split('T')[0],
    end: saturday.toISOString().split('T')[0]
  };
};

// Default new clinician data structure
export const getDefaultNewClinicianData = () => {
  const week = getCurrentMedicareWeek();
  return {
    clinician_name: '',
    hire_date: '',
    team: '',
    discipline: 'RNCM',
    period_start: week.start,
    period_end: week.end,
    total_visits: 0,
    average_time_per_visit: 0,
    expectation_min: 15,
    expectation_max: 18,
    comment: ''
  };
};

// Default new capacity data structure
export const getDefaultNewCapacityData = () => {
  const week = getCurrentMedicareWeek();
  return {
    clinician_name: '',
    hire_date: '',
    team: '',
    period_start: week.start,
    period_end: week.end,
    visit_capacity: 0,
    expectation_min: 15,
    expectation_max: 20,
    burnout_threshold: 20,  // High point for burnout
    max_capacity: 20,
    comment: ''
  };
};

// Default new discipline data structure
export const getDefaultNewDisciplineData = () => {
  const week = getCurrentMedicareWeek();
  return {
    period_start: week.start,
    period_end: week.end,
    rncm_visits: 0,
    lpn_visits: 0,
    wound_rn_visits: 0,
    prn_visits: 0
  };
};

// Default duplicate data structure - uses next Medicare week
export const getDefaultDuplicateData = () => {
  const nextWeek = getNextMedicareWeek();
  return {
    source_period_start: '',
    target_period_start: nextWeek.start,
    target_period_end: nextWeek.end
  };
};
